import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstagramIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Distributor",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't send your message right now. Please try again, or email info@mistydistribution.com directly.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-16 sm:py-20 px-6 md:px-12"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Left column */}
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <p className="text-misty-steel text-[10px] sm:text-xs tracking-[0.5em] uppercase font-sans">
              Get in Touch
            </p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-misty-text tracking-wide leading-[0.95]">
              Contact
            </h2>
            <div className="w-16 h-px bg-misty-steel/60 mt-2" />
          </div>

          <div
            className="flex flex-col gap-3"
            data-testid="contact-email-block"
          >
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-misty-steel" />
              <p className="text-misty-steel text-xs tracking-[0.3em] uppercase font-semibold">
                Email Us
              </p>
            </div>
            <div className="pl-7 flex flex-col gap-1">
              <a
                href="mailto:info@mistydistribution.com"
                className="text-misty-text hover:text-misty-steel transition-colors font-sans"
                data-testid="email-info"
              >
                info@mistydistribution.com
              </a>
              <a
                href="mailto:orders@mistydistribution.com"
                className="text-misty-text hover:text-misty-steel transition-colors font-sans"
                data-testid="email-orders"
              >
                orders@mistydistribution.com
              </a>
            </div>
          </div>

          <div
            className="flex flex-col gap-3"
            data-testid="contact-address-block"
          >
            <div className="flex items-center gap-3">
              <MapPin size={16} className="text-misty-steel" />
              <p className="text-misty-steel text-xs tracking-[0.3em] uppercase font-semibold">
                Head Office
              </p>
            </div>
            <p className="pl-7 text-misty-text font-sans leading-relaxed">
              250 Hall Street, Westend Office Park,
              <br />
              Building E, Centurion, 0157
            </p>
          </div>

          <div
            className="flex flex-col gap-3"
            data-testid="contact-social-block"
          >
            <div className="flex items-center gap-3">
              <Instagram size={16} className="text-misty-steel" />
              <p className="text-misty-steel text-xs tracking-[0.3em] uppercase font-semibold">
                Social Media
              </p>
            </div>
            <div className="pl-7 flex flex-col gap-3">
              <a
                href="https://instagram.com/goldbarvapessa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-misty-text hover:text-misty-steel transition-colors font-sans group w-fit"
                data-testid="instagram-goldbar"
              >
                <InstagramIcon />
                <span>@goldbarvapessa</span>
              </a>
              <a
                href="https://instagram.com/titanvapessa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-misty-text hover:text-misty-steel transition-colors font-sans group w-fit"
                data-testid="instagram-titan"
              >
                <InstagramIcon />
                <span>@titanvapessa</span>
              </a>
            </div>
          </div>
        </div>

        {/* Right column - form */}
        <div className="bg-misty-surface border border-white/5 p-8 sm:p-10">
          {!sent ? (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-6"
              data-testid="contact-form"
            >
              <h3 className="font-display text-3xl sm:text-4xl text-misty-text tracking-wide">
                Get In Touch
              </h3>
              <div className="flex flex-col gap-2">
                <label className="text-misty-steel text-xs tracking-[0.25em] uppercase font-semibold">
                  Name & Surname
                </label>
                <input
                  required
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  className="bg-misty-base border border-white/10 focus:border-misty-steel outline-none px-4 py-3 text-misty-text font-sans transition-colors"
                  data-testid="contact-input-name"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-misty-steel text-xs tracking-[0.25em] uppercase font-semibold">
                  Email
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="bg-misty-base border border-white/10 focus:border-misty-steel outline-none px-4 py-3 text-misty-text font-sans transition-colors"
                  data-testid="contact-input-email"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-misty-steel text-xs tracking-[0.25em] uppercase font-semibold">
                  Phone
                </label>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="bg-misty-base border border-white/10 focus:border-misty-steel outline-none px-4 py-3 text-misty-text font-sans transition-colors"
                  data-testid="contact-input-phone"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-misty-steel text-xs tracking-[0.25em] uppercase font-semibold">
                  I am a
                </label>
                <select
                  value={form.role}
                  onChange={update("role")}
                  className="bg-misty-base border border-white/10 focus:border-misty-steel outline-none px-4 py-3 text-misty-text font-sans transition-colors"
                  data-testid="contact-input-role"
                >
                  <option>Distributor</option>
                  <option>Retailer</option>
                  <option>Individual</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-misty-steel text-xs tracking-[0.25em] uppercase font-semibold">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="bg-misty-base border border-white/10 focus:border-misty-steel outline-none px-4 py-3 text-misty-text font-sans transition-colors resize-none"
                  data-testid="contact-input-message"
                />
              </div>
              {error && (
                <div
                  className="text-red-400 text-sm font-sans border border-red-500/30 bg-red-500/10 px-4 py-3"
                  data-testid="contact-error"
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="bg-misty-steel hover:bg-misty-steelHi text-misty-base px-8 py-4 font-sans font-semibold tracking-[0.2em] uppercase text-xs transition-colors mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          ) : (
            <div
              className="flex flex-col items-center text-center gap-6 py-12"
              data-testid="contact-success"
            >
              <div className="w-16 h-16 border border-misty-steel rotate-45 flex items-center justify-center">
                <svg
                  className="-rotate-45"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4A90D9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-display text-3xl text-misty-text tracking-wide">
                Message Sent
              </h3>
              <p className="text-misty-muted max-w-sm">
                Thanks — your message has been sent to our team. We'll be in
                touch shortly.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setForm({
                    name: "",
                    email: "",
                    phone: "",
                    role: "Distributor",
                    message: "",
                  });
                }}
                className="border border-white/15 hover:border-misty-steel text-misty-text hover:text-misty-steel px-6 py-3 font-sans tracking-[0.2em] uppercase text-[11px] transition-all"
                data-testid="contact-reset"
              >
                Send Another
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
